import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react'
import { Link, useLocation, Outlet } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { RootState } from '../../store'
import { logout } from '../../store/slices/authSlice'
import { setSchoolAdminLanguage, applyAccountLanguage } from '../../i18n'
import { useGetSchoolsQuery, schoolAdminApi } from '../../store/api/schoolAdminApi'
import { authApi } from '../../store/api/authApi'

type SchoolFilters = {
  preset: string
  dateFrom?: string
  dateTo?: string
  organizationId?: number | null
  projectId?: number | null
}

const FiltersContext = createContext<{
  filters: SchoolFilters
  setFilters: React.Dispatch<React.SetStateAction<SchoolFilters>>
} | null>(null)

export function useSchoolFilters() {
  const ctx = useContext(FiltersContext)
  if (!ctx) throw new Error('useSchoolFilters must be used within SchoolLayout')
  return ctx
}

const STORAGE_KEY = 'school_admin_org_id'

const SchoolLayout: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const { t, i18n } = useTranslation('school')
  const location = useLocation()
  const dispatch = useDispatch()
  const user = useSelector((s: RootState) => s.auth.user)
  const { data: schools = [] } = useGetSchoolsQuery()

  const savedOrg = Number(localStorage.getItem(STORAGE_KEY)) || null
  const [filters, setFilters] = useState<SchoolFilters>({
    preset: 'last30',
    organizationId: savedOrg,
  })

  useEffect(() => {
    if (!schools.length) return
    const stillValid = schools.some((s: any) => Number(s.id) === Number(filters.organizationId))
    if (!filters.organizationId || !stillValid) {
      const nextId = Number(schools[0].id)
      setFilters((f) => ({ ...f, organizationId: nextId, projectId: null }))
      localStorage.setItem(STORAGE_KEY, String(nextId))
    }
  }, [schools, filters.organizationId])

  // Keep UI language aligned with the signed-in org owner's ThinkClass preference.
  useEffect(() => {
    if (user?.role === 'ADMIN' && user.language) {
      applyAccountLanguage(user.language)
    }
  }, [user?.role, user?.language, user?.email])

  const nav = useMemo(() => [
    { name: t('nav.overview'), href: '/pending' },
    { name: t('nav.teachers'), href: '/pending/teachers' },
    { name: t('nav.students'), href: '/pending/students' },
    { name: t('nav.classes'), href: '/pending/classes' },
    { name: t('nav.reports'), href: '/pending/reports' },
  ], [t, i18n.language])

  const isActive = (href: string) => {
    if (href === '/pending') return location.pathname === '/pending' || location.pathname === '/pending/'
    return location.pathname.startsWith(href)
  }

  const onSchoolChange = (id: number) => {
    localStorage.setItem(STORAGE_KEY, String(id))
    setFilters((f) => ({ ...f, organizationId: id, projectId: null }))
  }

  return (
    <FiltersContext.Provider value={{ filters, setFilters }}>
      <div className="flex h-screen bg-gray-50">
        <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-6 border-b border-gray-100 space-y-3">
            <div>
              <img
                src="/thinkclass-logo-horizontal-white.png"
                alt="ThinkClass"
                className="h-8 w-auto object-contain"
              />
              <p className="text-xs text-gray-500 mt-2 truncate">{user?.email}</p>
            </div>
            {schools.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('nav.school')}</label>
                <select
                  value={filters.organizationId ?? ''}
                  onChange={(e) => onSchoolChange(Number(e.target.value))}
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  {schools.map((s: any) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <nav className="flex-1 px-4 py-4 space-y-1">
            {nav.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`block px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(item.href)
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-500'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t border-gray-100 space-y-2">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSchoolAdminLanguage('en', { manual: true })}
                className={`flex-1 text-xs py-1.5 rounded border ${i18n.language === 'en' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-600'}`}
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => setSchoolAdminLanguage('es', { manual: true })}
                className={`flex-1 text-xs py-1.5 rounded border ${i18n.language.startsWith('es') ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-600'}`}
              >
                ES
              </button>
            </div>
            <button
              type="button"
              onClick={() => {
                dispatch(logout())
                dispatch(authApi.util.resetApiState())
                dispatch(schoolAdminApi.util.resetApiState())
              }}
              className="w-full text-left text-sm text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md hover:bg-gray-50"
            >
              {t('nav.signOut')}
            </button>
          </div>
        </aside>
        <main className="flex-1 overflow-y-auto p-6">
          {children || <Outlet />}
        </main>
      </div>
    </FiltersContext.Provider>
  )
}

export default SchoolLayout
